import React from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Box, Button, Card, CardActions, CardContent, CardMedia, Chip, Typography } from '@mui/material';
import type { PublicProduct } from '../../services/storefrontService';
import { formatMoney } from './storefrontHelpers';
import { useStorefront } from './StorefrontContext';
import { useStorefrontT } from './useStorefrontT';
import MaterialSymbol from '../common/MaterialSymbol';

export const ProductPrice: React.FC<{ product: PublicProduct; showVat?: boolean }> = ({ product, showVat }) => {
    const { t, locale } = useStorefrontT();
    if (!product.price || product.price.isFree) {
        return <Typography component="span" sx={{ fontWeight: 600 }}>{t('product.free')}</Typography>;
    }
    return (
        <Box component="span">
            <Typography component="span" sx={{ fontWeight: 600 }}>{formatMoney(product.price.amount, product.price.currency, locale === 'en' ? 'en-GB' : 'de-DE')}</Typography>
            {showVat ? <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 0.75 }}>{t('product.vatIncluded')}</Typography> : null}
        </Box>
    );
};

const StorefrontProductCard: React.FC<{ product: PublicProduct }> = ({ product }) => {
    const { t } = useStorefrontT();
    const { slug, cart, addToCart } = useStorefront();
    const inCart = cart.includes(product.id);
    return (
        <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column' }} data-testid={`storefront-product-${product.id}`}>
            {product.thumbnailUrl ? <CardMedia component="img" height="160" image={product.thumbnailUrl} alt="" sx={{ objectFit: 'cover' }} /> : null}
            <CardContent sx={{ flexGrow: 1 }}>
                {product.isFeatured ? (
                    <Chip size="small" icon={<MaterialSymbol name="star" size={16} />} label={t('featured')} sx={{ mb: 1, bgcolor: 'var(--oa-paper, #f4f4f4)' }} />
                ) : null}
                <Typography variant="h6" component="h3" gutterBottom>
                    <RouterLink to={`/store/${slug}/products/${product.id}`} style={{ color: 'inherit', textDecoration: 'none', display: 'inline-block', minHeight: 'var(--oa-touch-target, 40px)', paddingTop: 6 }}>{product.title}</RouterLink>
                </Typography>
                {product.shortDescription ? <Typography variant="body2" color="text.secondary">{product.shortDescription}</Typography> : null}
            </CardContent>
            <CardActions sx={{ px: 2, pb: 2, justifyContent: 'space-between' }}>
                <ProductPrice product={product} />
                <Button size="small" variant={inCart ? 'outlined' : 'contained'} disabled={inCart} onClick={() => addToCart(product.id)}>
                    {inCart ? t('product.inCart') : t('product.addToCart')}
                </Button>
            </CardActions>
        </Card>
    );
};

export default StorefrontProductCard;
